import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../store/SharedProvider";
import { selectFloors, selectFloorsRestSet } from "../../../store/room/room-selectors";
import ReadOnlyProtectedForm from "../../Inputs/ReadOnlyProtectedForm";
import DialogWrapperWithClose from "../../General/DialogWrapperWithClose";
import { SaveIcon } from "../../../utils/icons/icons";
import ReadOnlyProtectedButton from "../../Inputs/ReadOnlyProtectedButton";
import useServerValidationResponseEffect from "../../../hooks/useServerValidationResponseEffect";
import HttpFetchStatus from "../../../enums/api/http-fetch-status";
import useValidatedInputEffect from "../../../hooks/useValidatedInputEffect";
import TextInput from "../../Inputs/TextInput";
import ValidationResult from "../../../interfaces/api/validation-result";

const FloorConfigurationDialog: React.FC<{ floorId: string | null; onClose: () => void }> = ({ floorId, onClose }) => {
  const dispatch = useSharedDispatch();

  const { patchFloor, patchFloorsRestSet } = useSharedActions();

  const floors = useSharedSelector((state) => selectFloors(state));
  const floorsRestSet = useSharedSelector((state) => selectFloorsRestSet(state));
  const selectedFloor = useMemo(() => floors.find((f) => f.id === floorId), [floors, floorId]);

  const { t } = useTranslation();

  const { resetToggle, removeServerError, getServerValidationResultOrNull } = useServerValidationResponseEffect({
    patchError: floorsRestSet.patchError,
    resetPatchHandler: () => dispatch(patchFloorsRestSet({ patchStatus: HttpFetchStatus.Idle, patchError: null })),
  });

  const floorNameValidator = (value: string): ValidationResult => {
    const existingFloorNames = floors.filter((f) => f.id !== floorId).map((r) => r.name) ?? [];

    if (existingFloorNames.includes(value)) {
      return { isValid: false, error: t("error-floor-duplicate-name") };
    }

    if (value.length > 80) {
      return { isValid: false, error: t("validation-error-name-too-long") };
    }

    return { isValid: true, error: null };
  };

  const {
    value: name,
    validationResult: nameValidationResult,
    valueChangeHandler: nameChangeHandler,
    setValue: setEditedFloorName,
  } = useValidatedInputEffect(
    selectedFloor?.name ?? "",
    floorNameValidator,
    (_) => () => {},
    getServerValidationResultOrNull("name"),
    removeServerError,
    resetToggle
  );

  useEffect(() => {
    if (selectedFloor?.name != null) {
      setEditedFloorName(selectedFloor.name);
    }
  }, [selectedFloor?.name, setEditedFloorName]);

  const saveFloorNameClickHandler = () => {
    if (floorId != null) {
      dispatch(patchFloor({ id: floorId, data: { name } }));
    }
  };

  return (
    <React.Fragment>
      <DialogWrapperWithClose
        open={floorId != null}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        title={t("floor-details")}
      >
        <ReadOnlyProtectedForm>
          <DialogContent>
            <TextInput
              label={t("input-label-name")}
              value={name}
              onChange={nameChangeHandler}
              validationResult={nameValidationResult}
            />
          </DialogContent>
          <DialogActions>
            <ReadOnlyProtectedButton
              variant="contained"
              color="primary"
              size="small"
              startIcon={<SaveIcon />}
              onClick={saveFloorNameClickHandler}
              disabled={selectedFloor?.name == null || !name.trim() || name === selectedFloor.name || !nameValidationResult.isValid}
            >
              {t("button-save")}
            </ReadOnlyProtectedButton>
          </DialogActions>
        </ReadOnlyProtectedForm>
      </DialogWrapperWithClose>
    </React.Fragment>
  );
};

export default FloorConfigurationDialog;
